import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { ReactNode, RefObject } from 'react';
import { MaterialIcon } from '../../../ui/MaterialIcon';
import type { Destination as CaptureDestination } from './destination';
import { destinationIcon, destinationLabel, destinationSearchCopy } from './destinationPickerUtils';
import { BowerSpinner } from '../../../ui/BowerSpinner';

interface DestinationPickerResultsProps extends DestinationContentProps {
  /** More collections on the server than shown (the list pages at 50). */
  readonly hasMore?: boolean;
  readonly isLoadingMore?: boolean;
  readonly onLoadMore?: () => void;
  readonly onCreate: () => void;
  readonly onQueryChange: (query: string) => void;
  readonly query: string;
  readonly searchRef: RefObject<HTMLInputElement | null>;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
}

interface DestinationContentProps {
  readonly collectionDestinations: readonly CaptureDestination[];
  readonly isLoading: boolean;
  readonly isNotesLoading: boolean;
  readonly isSearching: boolean;
  readonly noteDestinations: readonly CaptureDestination[];
  readonly onSelect: (destination: CaptureDestination) => void;
  readonly query: string;
  readonly selectedDestination: CaptureDestination | null;
  readonly showNotes: boolean;
  readonly visibleWorkspace: CaptureDestination | null;
}

interface DestinationOptionProps {
  readonly destination: CaptureDestination;
  readonly isSelected: boolean;
  readonly onSelect: (destination: CaptureDestination) => void;
  readonly resultPosition: number;
}

function DestinationOption(props: DestinationOptionProps) {
  const { destination, isSelected, onSelect, resultPosition } = props;
  const resultType = destinationLabel(destination).toLowerCase();
  // Only the create modal's rows carry these (`include=destination`); Capture's
  // never do, so its rows render exactly as before.
  const viewOnly = destination.type === 'project' && destination.canEdit === false;
  const locked = destination.type === 'project' && destination.privacyMode !== undefined;
  const parentName = destination.type === 'project' ? destination.parentName : undefined;
  return (
    <Button
      isDisabled={viewOnly}
      variant="unstyled"
      display="flex"
      w="full"
      h="auto"
      minH="54px"
      justifyContent="flex-start"
      gap={3}
      px={2}
      py={2.5}
      borderRadius="xl"
      bg={isSelected ? 'brand.surface' : 'transparent'}
      _hover={{ bg: isSelected ? 'brand.surface' : 'bg.subtle' }}
      _active={{ bg: 'bg.hover' }}
      aria-label={`Select ${resultType} result ${resultPosition}: ${destination.name}`}
      aria-pressed={isSelected}
      onClick={() => onSelect(destination)}
    >
      <Box
        w={9}
        h={9}
        borderRadius="lg"
        border="1px solid"
        borderColor="border.muted"
        bg="bg.canvas"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <MaterialIcon name={destinationIcon(destination)} boxSize={4} color="text.secondary" />
      </Box>
      <Box flex={1} minW={0} textAlign="left">
        <HStack spacing={1}>
          <Text fontSize="md" fontWeight="medium" color="text.primary" noOfLines={1}>
            {destination.name}
          </Text>
          {locked && (
            <MaterialIcon name="lock" boxSize={3.5} color="text.muted" aria-label="Private" />
          )}
        </HStack>
        {parentName && (
          <Text fontSize="xs" color="text.secondary" noOfLines={1}>
            Inside {parentName}
          </Text>
        )}
      </Box>
      {viewOnly && (
        <Text fontSize="xs" color="text.muted" flexShrink={0}>
          View only
        </Text>
      )}
      {!viewOnly && isSelected && (
        <MaterialIcon name="check" boxSize={5} color="brand.onSurface" flexShrink={0} />
      )}
      {!viewOnly && !isSelected && (
        <Box
          px={2}
          py={0.5}
          borderRadius="full"
          border="1px solid"
          borderColor="border.muted"
          flexShrink={0}
        >
          <Text fontSize="xs" color="text.muted">
            {destinationLabel(destination)}
          </Text>
        </Box>
      )}
    </Button>
  );
}

function DestinationSection({
  children,
  title,
}: {
  readonly children: ReactNode;
  readonly title: string;
}) {
  return (
    <>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="text.muted"
        letterSpacing="wider"
        textTransform="uppercase"
        px={2}
        py={2}
      >
        {title}
      </Text>
      {children}
    </>
  );
}

function DestinationSearchInput({
  onQueryChange,
  query,
  searchRef,
  showNotes,
}: {
  readonly onQueryChange: (query: string) => void;
  readonly query: string;
  readonly searchRef: RefObject<HTMLInputElement | null>;
  readonly showNotes: boolean;
}) {
  const copy = destinationSearchCopy(showNotes);
  return (
    <InputGroup size="lg">
      <InputLeftElement pointerEvents="none">
        <MaterialIcon name="search" boxSize={5} color="text.muted" />
      </InputLeftElement>
      <Input
        ref={searchRef}
        aria-label={copy.ariaLabel}
        placeholder={copy.placeholder}
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        variant="outline"
        borderRadius="xl"
        pl={10}
        _focus={{ borderColor: 'brand.primary', boxShadow: 'none' }}
        _focusVisible={{ borderColor: 'brand.primary', boxShadow: 'none' }}
      />
    </InputGroup>
  );
}

function CreateCollectionButton({ onCreate }: { readonly onCreate: () => void }) {
  return (
    <Button
      variant="unstyled"
      display="flex"
      h="auto"
      minH="52px"
      justifyContent="flex-start"
      gap={3}
      px={2}
      py={3}
      borderRadius="xl"
      _hover={{ bg: 'bg.subtle' }}
      _active={{ bg: 'bg.hover' }}
      aria-label="Create new collection"
      onClick={onCreate}
    >
      <Box
        w={9}
        h={9}
        borderRadius="full"
        bg="brand.muted"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <MaterialIcon name="add" boxSize={5} color="brand.onSurface" />
      </Box>
      <Text fontSize="md" fontWeight="semibold" color="brand.onSurface">
        Create new collection
      </Text>
    </Button>
  );
}

function DestinationContent({
  collectionDestinations,
  isLoading,
  isNotesLoading,
  isSearching,
  noteDestinations,
  onSelect,
  query,
  selectedDestination,
  showNotes,
  visibleWorkspace,
}: DestinationContentProps) {
  const collectionStart = visibleWorkspace ? 2 : 1;
  const noteStart = collectionStart + collectionDestinations.length;
  const hasResults =
    Boolean(visibleWorkspace) ||
    collectionDestinations.length > 0 ||
    (showNotes && noteDestinations.length > 0);
  if (isLoading) {
    return (
      <HStack justify="center" py={4} role="status" aria-label="Loading destinations">
        <BowerSpinner size="sm" />
      </HStack>
    );
  }

  return (
    <>
      {visibleWorkspace ? (
        <DestinationSection title="Workspace">
          <DestinationOption
            destination={visibleWorkspace}
            isSelected={selectedDestination?.id === visibleWorkspace.id}
            onSelect={onSelect}
            resultPosition={1}
          />
        </DestinationSection>
      ) : null}
      {collectionDestinations.length > 0 ? (
        <DestinationSection title="Collections">
          {collectionDestinations.map((destination, index) => (
            <DestinationOption
              key={destination.id}
              destination={destination}
              isSelected={selectedDestination?.id === destination.id}
              onSelect={onSelect}
              resultPosition={collectionStart + index}
            />
          ))}
        </DestinationSection>
      ) : null}
      {showNotes && isNotesLoading ? (
        <DestinationSection title="Notes">
          <HStack justify="center" py={4} role="status" aria-label="Loading notes">
            <BowerSpinner size="sm" />
          </HStack>
        </DestinationSection>
      ) : null}
      {showNotes && !isNotesLoading && noteDestinations.length > 0 ? (
        <DestinationSection title="Notes">
          {noteDestinations.map((destination, index) => (
            <DestinationOption
              key={destination.id}
              destination={destination}
              isSelected={selectedDestination?.id === destination.id}
              onSelect={onSelect}
              resultPosition={noteStart + index}
            />
          ))}
        </DestinationSection>
      ) : null}
      {!hasResults && isSearching && !isNotesLoading ? (
        <Text fontSize="sm" color="text.muted" textAlign="center" py={4}>
          No destinations match &ldquo;{query}&rdquo;
        </Text>
      ) : null}
    </>
  );
}

export function DestinationPickerResults({
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onCreate,
  onQueryChange,
  query,
  scrollContainerRef,
  searchRef,
  showNotes,
  ...content
}: DestinationPickerResultsProps) {
  return (
    <VStack gap={3} align="stretch" h="full" overflow="hidden">
      <DestinationSearchInput
        onQueryChange={onQueryChange}
        query={query}
        searchRef={searchRef}
        showNotes={showNotes}
      />
      <VStack ref={scrollContainerRef} gap={0} align="stretch" overflowY="auto" flex={1}>
        <CreateCollectionButton onCreate={onCreate} />
        <DestinationContent {...content} query={query} showNotes={showNotes} />
        {hasMore && onLoadMore && (
          <Button
            variant="ghost"
            size="sm"
            alignSelf="center"
            my={2}
            isLoading={isLoadingMore}
            onClick={onLoadMore}
          >
            Show more collections
          </Button>
        )}
      </VStack>
    </VStack>
  );
}
